// src/components/OurServices.js
import React from 'react';
import { Link } from 'react-router-dom';
import Header from './Header';
import Footer from './Footer';
import { useI18n } from '../i18n/I18nContext';
import { pickPageCopy, servicesCopy } from '../i18n/pageCopy';
import '../style/Homepage.css';
import tarotSpreadCraft from '../assets/images/tarot-spread-craft.jpg';
import tarotPageCups from '../assets/images/tarot-page-cups.jpg';
import tarotHermit from '../assets/images/tarot-hermit.jpg';
import tarotHierophant from '../assets/images/tarot-hierophant.jpg';

const OurServices = () => {
  const { language, t } = useI18n();
  const copy = pickPageCopy(language, servicesCopy);

  return (
    <div>
      <Header />
      <main className="info-page services-page">
        <section className="services-hero">
          <div className="services-hero-copy">
            <p className="eyebrow">{copy.heroEyebrow}</p>
            <h1>{copy.heroTitle}</h1>
            <p>{copy.heroBody}</p>
            <div className="hero-actions">
              <Link to="/register" className="primary-button">{copy.create}</Link>
              <Link to="/how-it-works" className="secondary-button">{copy.guide}</Link>
            </div>
          </div>
          <div className="services-hero-image">
            <img src={tarotSpreadCraft} alt={copy.heroAlt} />
          </div>
        </section>

        <section className="services-plain-summary">
          <div>
            <p className="eyebrow">{copy.plainEyebrow}</p>
            <h2>{copy.plainTitle}</h2>
          </div>
          <p>{copy.plainBody}</p>
        </section>

        <section className="services-pillars" aria-label={t('a11y.coreServices')}>
          {copy.pillars.map(([title, body], index) => (
            <article key={title}>
              <span>{String(index + 1).padStart(2, '0')}</span>
              <h2>{title}</h2>
              <p>{body}</p>
            </article>
          ))}
        </section>

        <section className="services-audience-grid">
          <div className="services-audience-card client-card">
            <div className="services-card-heading">
              <img src={tarotPageCups} alt={copy.clientAlt} />
              <div>
                <p className="eyebrow">{copy.clients}</p>
                <h2>{copy.clientTitle}</h2>
              </div>
            </div>
            <div className="services-feature-list">
              {copy.clientFeatures.map(([title, body]) => (
                <article key={title}>
                  <h3>{title}</h3>
                  <p>{body}</p>
                </article>
              ))}
            </div>
          </div>

          <div className="services-audience-card teller-card">
            <div className="services-card-heading">
              <img src={tarotHierophant} alt={copy.tellerAlt} />
              <div>
                <p className="eyebrow">{copy.tellers}</p>
                <h2>{copy.tellerTitle}</h2>
              </div>
            </div>
            <div className="services-feature-list">
              {copy.tellerFeatures.map(([title, body]) => (
                <article key={title}>
                  <h3>{title}</h3>
                  <p>{body}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="services-flow-section">
          <div className="section-heading">
            <p className="eyebrow">{copy.flowEyebrow}</p>
            <h2>{copy.flowTitle}</h2>
            <p>{copy.flowBody}</p>
          </div>
          <div className="services-flow-grid">
            {copy.flow.map(([number, title, body]) => (
              <article key={number}>
                <span>{number}</span>
                <h3>{title}</h3>
                <p>{body}</p>
              </article>
            ))}
          </div>
        </section>

        <section className="services-compare-section" aria-labelledby="services-compare-title">
          <div className="section-heading">
            <p className="eyebrow">{copy.includedEyebrow}</p>
            <h2 id="services-compare-title">{copy.includedTitle}</h2>
            <p>{copy.includedBody}</p>
          </div>
          <div className="services-compare-grid">
            <article className="services-compare-card included">
              <h3>{copy.includedHeading}</h3>
              {copy.included.map(([title, body]) => (
                <div key={title}>
                  <strong>{title}</strong>
                  <p>{body}</p>
                </div>
              ))}
            </article>
            <article className="services-compare-card excluded">
              <h3>{copy.excludedHeading}</h3>
              {copy.excluded.map(([title, body]) => (
                <div key={title}>
                  <strong>{title}</strong>
                  <p>{body}</p>
                </div>
              ))}
            </article>
          </div>
        </section>

        <section className="services-faq-section" aria-labelledby="services-faq-title">
          <div className="section-heading">
            <p className="eyebrow">{copy.faqEyebrow}</p>
            <h2 id="services-faq-title">{copy.faqTitle}</h2>
          </div>
          <div className="services-faq-list">
            {copy.faq.map(([question, answer]) => (
              <article key={question}>
                <h3>{question}</h3>
                <p>{answer}</p>
              </article>
            ))}
          </div>
        </section>

        <section className="services-boundary-section">
          <div>
            <p className="eyebrow">{copy.boundaryEyebrow}</p>
            <h2>{copy.boundaryTitle}</h2>
            <p>{copy.boundaryBody}</p>
            <Link to="/terms" className="secondary-button">{copy.terms}</Link>
          </div>
          <img src={tarotHermit} alt={copy.boundaryAlt} />
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default OurServices;
